// Rechnungswerkzeug (/rechnung): Antworten des Servers (server/src/modules/rechnungstool)
import type { Tarif } from '../billtoro/types';
import type { Waehrung } from './waehrung';
import type { Uhr } from './zeiten/ZeitDaten';

export interface Absender {
  name: string;
  street: string;
  zip: string;
  city: string;
  email: string | null;
  phone: string | null;
  taxNumber: string | null;
  vatId: string | null;
  accountHolder: string;
  iban: string;
  bic: string | null;
  bankName: string | null;
  kleinunternehmer: boolean;
  // Kleinunternehmer, freiwillige Angaben: gespeichert im Jahr, Umsatz Vorjahr, in diesem Jahr gegründet, Umsatz außerhalb
  kuJahr: number | null;
  kuVorjahr: number | null;
  kuGruendung: boolean;
  kuExtra: number;
  paymentDays: number;
  // Pflichtangaben auf Geschäftsbriefen je Rechtsform (fehlen bei älteren Angaben und im Vorschlag)
  rechtsform?: Rechtsform | null;
  sitz?: string | null;
  registergericht?: string | null;
  registernr?: string | null;
  vertretenDurch?: string | null;
  // false: Vorschlag aus den Firmendaten, noch nie gespeichert
  saved?: boolean;
}

export type Rechtsform = 'einzel' | 'ek' | 'gbr' | 'ohg' | 'kg' | 'gmbh_co_kg' | 'gmbh' | 'ug' | 'ag' | 'andere';

export type RtArt = 'normal' | 'abschlag' | 'schluss';
export type KundenArt = 'firma' | 'privat' | 'behoerde';
// Umsatzsteuer einer Position: 19 %, 7 %, 0 % (dazu ein eigener Hinweis auf der Rechnung, z. B. zu § 13b oder Reverse
// charge EU; was er bedeutet, erkennt der Server)
export type Rate = 19 | 7 | 0;
// wie gespeichert: dazu „13b“ und „eu“ (Reverse charge EU) von früher (alte Rechnungen, Angebote, Vorlagen); im
// Formular wird daraus 0 %
export type AlteRate = Rate | '13b' | 'eu';
export type ZahlArt = 'zahlung' | 'bauabzug' | 'ausbuchung' | 'erstattung';

export interface ZahlEintrag {
  id: string;
  art: ZahlArt;
  betrag: number;
  datum: string;
  notiz: string | null;
  quelle: 'hand' | 'abgleich' | 'uebernahme';
  // Gegeneintrag zu einem früheren Eintrag, oder selbst zurückgenommen
  ruecknahme: boolean;
  zurueckgenommen: boolean;
}

// Rechnung, Stornorechnung oder Rechnungskorrektur (mindert eine Rechnung, KorrekturDialog.tsx)
export type RtKind = 'rechnung' | 'storno' | 'korrektur';

export interface RtItem {
  id: string;
  kind: RtKind;
  art: RtArt;
  number: string;
  invoiceDate: string;
  dueDate: string;
  // Zahlungen ab diesem Tag (im Voraus datiert: ab dem Tag, an dem die Rechnung geschrieben wurde)
  zahlbarAb: string;
  customer: string;
  // Kunde der Kundenliste (fehlt nur, wenn er aus der Liste entfernt wurde)
  kundeId: string | null;
  // aus dieser wiederkehrenden Rechnung erzeugt
  aboId: string | null;
  skonto: Skonto | null;
  // fehlt bei Rechnungen von vor der Kundenart
  kundeArt: KundenArt | null;
  country: string;
  auftrag: string | null;
  // Rechnungsbetrag (Schlussrechnung: Rest nach Abzug der Abschläge)
  amount: number;
  // Währung der Beträge und Kurs (Euro je 1 Einheit; Euro: 1). Kennzahlen rechnen in Euro um (waehrung.ts inEurZahl)
  waehrung: Waehrung;
  kurs: number;
  status: 'offen' | 'teilweise' | 'bezahlt' | 'storniert';
  paidOn: string | null;
  // mit Gebühren und Zinsen, davon beglichen (davon ausgebucht, ohne Geld), noch offen, zu erstatten; 15 % für den
  // Bauabzug (bei § 13b mit der Umsatzsteuer des Kunden)
  forderung: number;
  bezahlt: number;
  ausgebucht: number;
  offen: number;
  erstattung: number;
  bauabzug: number;
  // Schlussrechnung: Nummern der abgezogenen Abschläge
  abzuege: string[];
  // Rechnungskorrekturen: zusammen gutgeschrieben (brutto, positiv) und einzeln (Betrag negativ)
  korrigiert: number;
  korrekturen: { id: string; number: string; date: string; amount: number }[];
  zahlungen: ZahlEintrag[];
  stornoNumber: string | null;
  // Storno oder Rechnungskorrektur: Nummer der Rechnung, die sie mindert
  stornoOfNumber: string | null;
  // E-Rechnung: ZUGFeRD (im PDF) oder XRechnung; leer bei Rechnungen von vor der E-Rechnung
  eFormat: EFormat | null;
  mahnungen: Mahnung[];
  // per E-Mail verschickt (neueste zuerst)
  versand: Versand[];
  // per Post verschickt (neueste zuerst, PostDialog.tsx)
  post: PostEintrag[];
  // zuletzt über das Teilen-Menü des Geräts geteilt oder heruntergeladen (Zeitpunkt ISO), null: nie
  geteilt: { am: string; weg: 'teilen' | 'download' } | null;
  // aus Auftragsbestätigung oder Lieferscheinen geschrieben (aus) und Lieferscheine zu dieser Rechnung (nur gültige)
  vorgang: { aus: { art: 'auftrag' | 'lieferschein'; id: string; number: string }[]; lieferscheine: { id: string; number: string }[] } | null;
  // nicht automatisch mahnen (Mahnlauf)
  mahnstopp: boolean;
  // Billtoro: aus Zeiten geschrieben (Anzahl, Minuten; auch nach dem Storno); sonst null
  zeiten?: { anzahl: number; minuten: number } | null;
  // Billtoro: so viele Stundenzettel aus dem Stapel liegen zu dieser Rechnung beim Kunden (fehlt: keine)
  stundenzettel?: number;
  // Mahnlauf eingeschaltet und Rechnung offen: nächste Stufe ab wann (heute fällig?) oder warum nicht automatisch
  mahnlauf: { stufe: 1 | 2 | 3; ab: string; faellig: boolean } | { grund: string } | null;
}

// Brief per Post: neu (wird übergeben), uebergeben (beim Anbieter), verschickt (gedruckt und an die Post), storniert,
// fehler; test: Testbetrieb des Anbieters (nichts gedruckt, nichts berechnet); kanzlei: die Kanzlei druckt selbst
// (uebergeben = liegt bei der Kanzlei, storniert = von der Kanzlei nicht verschickt)
export interface PostEintrag {
  status: 'neu' | 'uebergeben' | 'verschickt' | 'storniert' | 'fehler';
  am: string;
  uebergebenAm: string | null;
  verschicktAm: string | null;
  test: boolean;
  kanzlei: boolean;
}

export interface Versand {
  // Zeitpunkt (ISO) und Empfänger (mehrere mit Komma)
  am: string;
  an: string;
}

export type EFormat = 'zugferd' | 'xrechnung';

export interface Mahnung {
  id: string;
  level: 1 | 2 | 3;
  date: string;
  dueDate: string;
  fee: number;
  pauschale: boolean;
  zins: number;
  open: number;
  // zuletzt per E-Mail verschickt (ISO), null: noch nicht
  versendetAm: string | null;
}

export const MAHN_TITLES = ['Zahlungserinnerung', 'Mahnung', 'Letzte Mahnung'] as const;

// Empfänger der Rechnung laut Stundenzettel (Auftraggeber), wie die KI ihn gelesen hat
export interface StundenEmpfaenger {
  name: string | null;
  strasse: string | null;
  plz: string | null;
  ort: string | null;
}

// Vorschlag der KI aus einem Stundenzettel
export type StundenVorschlag =
  | { found: false; hinweis: string }
  | {
      found: true;
      // Empfänger und Objekt in einer Zeile zum Anzeigen; getrennt in empfaenger und objekt
      kunde: string | null;
      empfaenger?: StundenEmpfaenger | null;
      objekt?: string | null;
      eintraege: { datum: string | null; name: string | null; taetigkeit: string | null; stunden: number }[];
      hinweis: string | null;
    };

export interface Overview {
  firm: { id: string; name: string };
  today: string;
  // nächste automatische Rechnungsnummer je Jahr (Vorjahr bis nächstes Jahr), steht im leeren Feld „Rechnungsnummer“
  naechsteNummer: Record<string, string>;
  // KI eingerichtet und für diese Firma an (Stundenzettel, Kontoauszug); kiTest: Testbetrieb, nicht nur EU
  ki: boolean;
  kiTest: boolean;
  // Kundenliste (nach Name)
  kunden: Kunde[];
  // Leistungen und Preise früherer Rechnungen (neueste zuerst) für Preisvorschläge je Objekt
  verlauf: Verlauf[];
  // Abschlagsrechnungen, die eine Schlussrechnung noch abziehen kann
  abschlaege: Abschlag[];
  // Kleinunternehmer: Umsatz dieses Jahres und Grenze, Umsatz im Vorjahr (vorjahrUeber: über 25.000 €; vorjahrAngabe:
  // die eigene Angabe, sonst nach den Zahlungseingängen gezählt; vorjahrFehlt: nichts über das Vorjahr bekannt, dann
  // 25.000 € wie im ersten Jahr), nahe der Grenze (server kleinunternehmer.ts)
  ku: {
    jahr: number;
    grenze: number;
    umsatz: number;
    vorjahr: number;
    vorjahrUeber: boolean;
    vorjahrAngabe: boolean;
    vorjahrFehlt: boolean;
    warnen: boolean;
  } | null;
  absender: Absender;
  // zuletzt benutzter Hinweis bei 0 % USt (steht im Formular, sobald eine Position 0 % hat)
  hinweis0: string | null;
  // Briefversand per Post eingerichtet (Knopf „Per Post versenden“), Testbetrieb, die Kanzlei druckt selbst, Preis je
  // Brief netto
  post: { an: boolean; test: boolean; kanzlei: boolean; preis: number };
  items: RtItem[];
  // Billtoro: Tarif der Firma mit Grenzen, Verbrauch und Rolle (billtoro/types.ts). Kanzlei: null oder fehlt.
  tarif?: Tarif | null;
  // Mahnläufe (Mahnlauf.tsx): an, Art, pausiert, heute fällig, nicht per E-Mail verschickt; null: nie eingestellt
  mahnlauf: MahnlaufKurz | null;
  // Billtoro: die eigene laufende Uhr dieser Firma (Kopf-Chip, Übersicht) und die Uhr des Servers; Kanzlei: null
  zeiten?: { uhr: Uhr | null; jetzt: string } | null;
  // Billtoro: Jahre mit Ausgaben oder Kassenbuch, neueste zuerst (der Export für die Betriebsprüfung hat sie auch ohne
  // Rechnungen); Kanzlei: leer
  ausgabenJahre?: number[];
}

export interface MahnlaufKurz {
  an: boolean;
  modus: 'vorschlagen' | 'senden';
  pausiert: boolean;
  faellig: number;
  probleme: number;
}

export interface Abschlag {
  id: string;
  number: string;
  invoiceDate: string;
  customer: string;
  zip: string;
  auftrag: string | null;
  kleinunternehmer: boolean;
  net: number;
  vat: number;
  gross: number;
}

export interface Kunde {
  // Kunde der Kundenliste (fehlt bei Kunden aus einer Vorlage ohne Liste)
  id?: string;
  nummer?: number;
  name: string;
  extra: string | null;
  street: string;
  zip: string;
  city: string;
  country?: string;
  art?: KundenArt;
  vatId?: string | null;
  // E-Rechnung: E-Mail und Leitweg-ID (XRechnung); xrechnung: die letzte Rechnung an ihn war eine XRechnung
  email?: string | null;
  buyerRef?: string | null;
  xrechnung?: boolean;
  // nur in der Kundenliste: Kopie der E-Mails an, Telefon, Zahlungsziel nur für diesen Kunden, Notiz
  emailCc?: string | null;
  phone?: string | null;
  paymentDays?: number | null;
  notiz?: string | null;
  // nicht automatisch mahnen (Mahnlauf)
  mahnstopp?: boolean;
  // Währung für neue Belege (nur in der Kundenliste; fehlt: Euro)
  waehrung?: Waehrung;
}

export interface Skonto {
  prozent: number;
  tage: number;
}

// Position wie im Formular: Preis in Euro mit bis zu 4 Nachkommastellen (z. B. 0,085 je m²), Rabatt in Prozent
// (über 0 bis 100, bis 2 Nachkommastellen; fehlt: kein Rabatt). Steuer wie gespeichert (formular.ts rowOf macht aus
// „13b“ 0 %)
export interface Position {
  description: string;
  quantity: number;
  unit: string;
  price: number;
  rate: AlteRate;
  discount?: number;
  // Artikel aus „Leistungen und Artikel“, aus dem die Position übernommen ist (Lager; reicht durch die ganze Kette)
  artikelId?: string;
}

export interface Vorlage {
  customer: Kunde;
  art: RtArt;
  auftrag: string | null;
  items: Position[];
  site: string | null;
  note: string | null;
  // Hinweis bei 0 % USt (fehlt bei älteren Rechnungen)
  steuerHinweis?: string | null;
  eFormat: EFormat;
  // Skonto der Rechnung (fehlt bei älteren Rechnungen: null)
  skonto: Skonto | null;
  // für „Neu schreiben“: Kunde der Kundenliste, Leistungszeitraum und Zahlungsziel (Tage) der alten Rechnung
  kundeId: string | null;
  serviceFrom: string;
  serviceTo: string | null;
  paymentDays: number;
  // aus dieser Auftragsbestätigung bzw. diesen Lieferscheinen (gültige), sonst aus diesem Angebot: „Neu schreiben“
  // verbindet die neue Rechnung wieder
  angebotId?: string | null;
  auftragId?: string | null;
  lieferscheinIds?: string[];
  // Währung der alten Rechnung (der Kurs wird neu vorgeschlagen)
  waehrung?: Waehrung;
  // Billtoro: aus diesen Zeiten (nur wenn der Login sie alle sieht; sonst leer und zeitenFremd)
  zeitIds?: string[];
  zeitenFremd?: boolean;
}

export interface Verlauf {
  number: string;
  invoiceDate: string;
  customer: string;
  street: string;
  city: string;
  site: string | null;
  items: Position[];
  // Hinweis bei 0 % USt der Rechnung (fehlt ohne 0 %)
  steuerHinweis?: string | null;
  // Preise in dieser Währung (Empfehlungen nur aus Rechnungen derselben Währung)
  waehrung?: Waehrung;
}

// Abgleich mit dem Kontoauszug
export type TrefferArt = 'sicher' | 'wahrscheinlich' | 'betrag_abweichend' | 'nur_betrag';
export interface Zahlung {
  datum: string | null;
  betrag: number;
  name: string | null;
  zweck: string | null;
}
export type Abgleich =
  | { found: false; hinweis: string }
  | {
      found: true;
      // ki: die KI hat gelesen (PDF, Foto, unbekanntes Format); format: erkanntes Bankformat (ohne KI)
      ki: boolean;
      format: string | null;
      hinweis: string | null;
      eingaenge: number;
      offen: number;
      treffer: {
        art: TrefferArt;
        // möglicherweise Bauabzug: so viel hat der Kunde einbehalten
        bauabzug: number | null;
        // fristgerecht mit Skonto gezahlt: so viel wird als Skonto ausgebucht
        skonto: number | null;
        zahlung: Zahlung;
        rechnung: { id: string; number: string; customer: string; invoiceDate: string; amount: number; open: number };
      }[];
      // Eingänge, die schon bei einer Rechnung eingetragen sind (z. B. derselbe Auszug noch einmal)
      schonEingetragen: number;
      // schon: Nummer der Rechnung, bei der dieser Eingang schon eingetragen ist
      ohneTreffer: (Zahlung & { schon: string | null })[];
    };

// Rechnungskorrektur (KorrekturDialog.tsx): was sich gutschreiben lässt (GET …/rechnung/:id/korrektur)
export interface KorrekturStand {
  number: string;
  invoiceDate: string;
  art: RtArt;
  kleinunternehmer: boolean;
  // geht nicht: grund ist die Meldung dazu
  moeglich: boolean;
  grund: string | null;
  betrag: number;
  gutgeschrieben: number;
  bezahlt: number;
  offen: number;
  erstattung: number;
  // Positionen der Rechnung mit dem, was noch übrig ist (Menge und netto)
  positionen: {
    pos: number;
    description: string;
    unit: string;
    quantity: number;
    price: number;
    discount: number | null;
    rate: number;
    key: string;
    net: number;
    nochMenge: number;
    nochNetto: number;
  }[];
  // Steuerarten der Rechnung (für freie Beträge) mit dem Rest netto
  steuern: { key: string; label: string; nochNetto: number }[];
  korrekturen: { id: string; number: string; date: string; amount: number }[];
}

// Zusammenfassung vor dem Schreiben (POST …/korrektur/pruefen): Beträge der Korrektur negativ
export interface KorrekturPruefung {
  netto: number;
  ust: number;
  brutto: number;
  gruppen: { key: string; netto: number; ust: number }[];
  rechnungNeu: number;
  offen: number;
  erstattung: number;
  voll: boolean;
}
